import type { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import type { Moment } from 'moment';

import { FilterOperator, isCompositeFilterDescriptor, normalizeFilters } from '@progress/kendo-data-query';
import { DEFAULT_FILTER } from '@ricoh/ria.client.core/constants/component/grid';
import DateTime from '@ricoh/ria.client.core/plugins/moment';
import { getStringValue } from '@ricoh/ria.client.core/utils/data/object';

import type { Filter, TransformFilterDateOptions } from './query.type';

/**
 * Flat Filters.
 *
 * @param {Filter} source Source.
 *
 * @returns {FilterDescriptor} Filter descriptors.
 */
export function flatFilters(source: Filter): FilterDescriptor[] {
  const composite = normalizeFilters(source ?? DEFAULT_FILTER);

  return (function build(target: FilterDescriptor[], filters: Filter[]): FilterDescriptor[] {
    filters.forEach((item) => {
      if (!item) {
        return;
      }

      if (isCompositeFilterDescriptor(item)) {
        build(target, item.filters);
        return;
      }

      if (!item.field) {
        return;
      }

      target.push(item);
    });

    return target;
  })([], composite.filters);
}

/**
 * Add Filter.
 *
 * @param {Filter} source Source.
 * @param {FilterDescriptor} filter Added FilterDescriptor .
 *
 * @returns {CompositeFilterDescriptor} FilterDescriptor within added descriptors.
 */
export function addFilter(source: Filter, filter: FilterDescriptor): CompositeFilterDescriptor {
  const composite = normalizeFilters(source ?? DEFAULT_FILTER);
  composite.filters.push(filter);

  return composite;
}

/**
 * Remove Filter.
 *
 * @template TData Data type.
 * @param {Filter} source Source.
 * @param {(keyof TData)[]} fields Removed fields.
 *
 * @returns {CompositeFilterDescriptor} FilterDescriptor within removed descriptors.
 */
export function removeFilters<TData>(source: Filter, ...fields: (keyof TData)[]): CompositeFilterDescriptor {
  const composite = normalizeFilters(source ?? DEFAULT_FILTER);

  const createComposite = (logic: CompositeFilterDescriptor['logic']) => ({
    logic,
    filters: [],
  });

  return (function build(target: CompositeFilterDescriptor, filters: Filter[]): CompositeFilterDescriptor {
    filters.forEach((item) => {
      if (!item) {
        return;
      }

      if (isCompositeFilterDescriptor(item)) {
        const current = build(createComposite(item.logic), item.filters);

        if (current.filters.length) {
          target.filters.push(current);
        }

        return;
      }

      if (!item.field || fields.length === 0) {
        return;
      }

      const currentField = getStringValue(item.field);
      if (fields.includes(currentField)) {
        return;
      }

      target.filters.push(item);
    });

    return target;
  })(createComposite(composite.logic), composite.filters);
}

/** TransformFilters. */
export const transformFilters = {
  date: (filterDescriptor: FilterDescriptor, { withoutTimeZone }: TransformFilterDateOptions): FilterDescriptor[] => {
    const { field, operator, value, ignoreCase } = filterDescriptor;
    if (!field || !(value instanceof Date)) {
      return [filterDescriptor];
    }

    const toJSON = (date: Moment) => {
      const json = date.toJSON();

      return withoutTimeZone ? json.replace('Z', '') : json;
    };

    const start = DateTime(value).startOf('date');
    const end = DateTime(value).endOf('date');

    switch (getStringValue(operator)) {
      case FilterOperator.EqualTo: {
        return [
          {
            field,
            operator: FilterOperator.GreaterThanOrEqual,
            value: toJSON(start),
            ignoreCase,
          },
          {
            field,
            operator: FilterOperator.LessThanOrEqual,
            value: toJSON(end),
            ignoreCase,
          },
        ];
      }

      case FilterOperator.NotEqualTo: {
        return [
          {
            field,
            operator: FilterOperator.LessThan,
            value: toJSON(start),
            ignoreCase,
          },
          {
            field,
            operator: FilterOperator.GreaterThan,
            value: toJSON(end),
            ignoreCase,
          },
        ];
      }

      case FilterOperator.GreaterThan: {
        return [
          {
            field,
            operator: FilterOperator.GreaterThan,
            value: toJSON(end),
            ignoreCase,
          },
        ];
      }

      case FilterOperator.GreaterThanOrEqual: {
        return [
          {
            field,
            operator: FilterOperator.GreaterThanOrEqual,
            value: toJSON(start),
            ignoreCase,
          },
        ];
      }

      case FilterOperator.LessThan: {
        return [
          {
            field,
            operator: FilterOperator.LessThan,
            value: toJSON(start),
            ignoreCase,
          },
        ];
      }

      case FilterOperator.LessThanOrEqual: {
        return [
          {
            field,
            operator: FilterOperator.LessThanOrEqual,
            value: toJSON(end),
            ignoreCase,
          },
        ];
      }

      default: {
        return [filterDescriptor];
      }
    }
  },
};
